
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useToast } from "@/hooks/use-toast";
import { useRingCentralSettings } from "@/hooks/useRingCentralSettings";
import { Loader2, Phone, CheckCircle2, ChevronDown } from "lucide-react";

const DEFAULT_SERVER = "https://platform.ringcentral.com";

type FieldErrors = Partial<Record<"clientId" | "clientSecret" | "jwtToken" | "phoneNumber" | "serverUrl", string>>;

const normalizePhoneNumber = (value: string) => value.replace(/[\s()-]/g, "");

const RingCentralSettings = () => {
  const { settings, loading, updateSettings } = useRingCentralSettings();
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [jwtToken, setJwtToken] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [serverUrl, setServerUrl] = useState(DEFAULT_SERVER);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formOpen, setFormOpen] = useState(false);
  const { toast } = useToast();

  const validate = (): FieldErrors => {
    const next: FieldErrors = {};
    if (!clientId.trim()) next.clientId = "Enter your Client ID.";
    if (!clientSecret.trim()) next.clientSecret = "Enter your Client Secret.";
    if (!jwtToken.trim()) next.jwtToken = "Enter your JWT token.";

    if (!phoneNumber.trim()) next.phoneNumber = "Enter your RingCentral phone number.";
    else if (!/^\+[1-9]\d{6,14}$/.test(normalizePhoneNumber(phoneNumber)))
      next.phoneNumber = "Use a full number with country code, like +14155551234.";

    if (!serverUrl.trim()) next.serverUrl = "Enter a server URL, or keep the default.";
    else if (!/^https:\/\//i.test(serverUrl.trim()))
      next.serverUrl = "The server URL should start with https://.";

    return next;
  };

  const handleSaveSettings = async () => {
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) {
      toast({
        title: "Check the highlighted fields",
        description: "A couple of values need a second look before we can save.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    const success = await updateSettings({
      client_id: clientId,
      client_secret: clientSecret,
      jwt_token: jwtToken,
      phone_number: normalizePhoneNumber(phoneNumber),
      server_url: serverUrl,
      is_active: true
    });

    if (success) {
      setClientId("");
      setClientSecret("");
      setJwtToken("");
      setPhoneNumber("");
      setServerUrl(DEFAULT_SERVER);
      setErrors({});
      setFormOpen(false);
    }
    setIsSaving(false);
  };

  const handleToggleActive = async (checked: boolean) => {
    if (settings) {
      await updateSettings({ is_active: checked });
    }
  };

  const inputClass = (hasError: boolean) =>
    `mt-2 h-11 ${hasError ? "border-status-danger focus-visible:ring-status-danger" : ""}`;

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const credentialForm = (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between">
          <Label htmlFor="client-id" className="text-sm font-medium text-foreground">Client ID</Label>
          <span className="text-xs font-medium text-status-danger">Required</span>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">From your RingCentral app credentials.</p>
        <Input
          id="client-id"
          type="text"
          autoComplete="off"
          placeholder="Enter your Client ID"
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          aria-invalid={!!errors.clientId}
          className={inputClass(!!errors.clientId)}
        />
        {errors.clientId && <p className="mt-1.5 text-xs text-status-danger">{errors.clientId}</p>}
      </div>

      <div>
        <div className="flex items-center justify-between">
          <Label htmlFor="client-secret" className="text-sm font-medium text-foreground">Client Secret</Label>
          <span className="text-xs font-medium text-status-danger">Required</span>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">Stored encrypted. It is never shown again after saving.</p>
        <Input
          id="client-secret"
          type="password"
          autoComplete="off"
          placeholder="Enter your Client Secret"
          value={clientSecret}
          onChange={(e) => setClientSecret(e.target.value)}
          aria-invalid={!!errors.clientSecret}
          className={inputClass(!!errors.clientSecret)}
        />
        {errors.clientSecret && <p className="mt-1.5 text-xs text-status-danger">{errors.clientSecret}</p>}
      </div>

      <div>
        <div className="flex items-center justify-between">
          <Label htmlFor="jwt-token" className="text-sm font-medium text-foreground">JWT token</Label>
          <span className="text-xs font-medium text-status-danger">Required</span>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">Generated for your extension. Stored encrypted.</p>
        <Input
          id="jwt-token"
          type="password"
          autoComplete="off"
          placeholder="Enter your JWT token"
          value={jwtToken}
          onChange={(e) => setJwtToken(e.target.value)}
          aria-invalid={!!errors.jwtToken}
          className={inputClass(!!errors.jwtToken)}
        />
        {errors.jwtToken && <p className="mt-1.5 text-xs text-status-danger">{errors.jwtToken}</p>}
      </div>

      <div>
        <div className="flex items-center justify-between">
          <Label htmlFor="ringcentral-phone-number" className="text-sm font-medium text-foreground">Phone number</Label>
          <span className="text-xs font-medium text-status-danger">Required</span>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">The RingCentral number texts are sent from.</p>
        <Input
          id="ringcentral-phone-number"
          type="tel"
          inputMode="tel"
          autoComplete="off"
          placeholder="+14155551234"
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          aria-invalid={!!errors.phoneNumber}
          className={inputClass(!!errors.phoneNumber)}
        />
        {errors.phoneNumber && <p className="mt-1.5 text-xs text-status-danger">{errors.phoneNumber}</p>}
      </div>

      <div>
        <div className="flex items-center justify-between">
          <Label htmlFor="server-url" className="text-sm font-medium text-foreground">Server URL</Label>
          <span className="text-xs text-muted-foreground">Defaults to production</span>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">Leave as is unless RingCentral told you otherwise.</p>
        <Input
          id="server-url"
          type="url"
          autoComplete="off"
          placeholder={DEFAULT_SERVER}
          value={serverUrl}
          onChange={(e) => setServerUrl(e.target.value)}
          aria-invalid={!!errors.serverUrl}
          className={inputClass(!!errors.serverUrl)}
        />
        {errors.serverUrl && <p className="mt-1.5 text-xs text-status-danger">{errors.serverUrl}</p>}
      </div>

      <Button
        onClick={handleSaveSettings}
        disabled={isSaving}
        className="h-11 w-full"
      >
        {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        {settings ? "Update credentials" : "Save credentials"}
      </Button>
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Phone className="h-5 w-5 text-muted-foreground" />
          RingCentral
        </CardTitle>
        <CardDescription>
          Connect your RingCentral account so live texts can go out.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {settings ? (
          <>
            <div className="flex items-start justify-between gap-4 rounded-lg border border-status-success/30 bg-status-success/5 p-4">
              <div className="flex min-w-0 items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-status-success" />
                <div className="min-w-0">
                  <Label className="text-sm font-medium text-foreground">RingCentral connected</Label>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {settings.is_active ? "Active and ready for live mode." : "Saved, but currently switched off."}
                  </p>
                </div>
              </div>
              <Switch checked={settings.is_active} onCheckedChange={handleToggleActive} aria-label="Toggle RingCentral active" />
            </div>

            {/* Masked summary — no secrets shown */}
            <dl className="grid grid-cols-1 gap-2 text-xs">
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">Client ID</dt>
                <dd className="truncate font-mono text-foreground">
                  {settings.client_id.slice(0, 6)}&hellip;{settings.client_id.slice(-4)}
                </dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">Phone number</dt>
                <dd className="font-mono text-foreground">{settings.phone_number}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">Server</dt>
                <dd className="truncate font-mono text-foreground">{settings.server_url}</dd>
              </div>
            </dl>

            <Collapsible open={formOpen} onOpenChange={setFormOpen}>
              <CollapsibleTrigger asChild>
                <Button variant="outline" className="h-11 w-full justify-between">
                  Update credentials
                  <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${formOpen ? "rotate-180" : ""}`} />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-4">
                <p className="mb-4 rounded-lg border border-border bg-muted/40 p-3 text-xs leading-relaxed text-muted-foreground">
                  Leave these fields blank to keep your saved credentials. To save new ones, re-enter every required
                  field — a partial entry is rejected and your saved credentials stay as they are.
                </p>
                {credentialForm}
              </CollapsibleContent>
            </Collapsible>
          </>
        ) : (
          credentialForm
        )}

        <div className="rounded-lg border border-border bg-muted/40 p-4">
          <h4 className="mb-2 text-sm font-medium text-foreground">Where to find these</h4>
          <ol className="list-inside list-decimal space-y-1 text-xs text-muted-foreground">
            <li>Create a RingCentral developer app.</li>
            <li>Copy the Client ID and Client Secret from the app settings.</li>
            <li>Generate a JWT token for your extension.</li>
            <li>Use a phone number you have verified in RingCentral.</li>
          </ol>
        </div>
      </CardContent>
    </Card>
  );
};

export default RingCentralSettings;
